'use client';

import { useEffect } from 'react';
import { z } from 'zod';
import { useStore } from 'zustand';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { faHouseChimneyMedical } from '@fortawesome/pro-light-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { useToast } from '@/components/ui/use-toast';
import { useLoading } from '@/hooks/use-loading.hook';
import { Icon } from '@/components/ui/icon.component';
import { Input } from '@/components/ui/input/input.component';
import { userLocationStore } from '@/store/user-location.store';
import { authStore } from '@/store/auth.store';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form.component';
import { completeNameValidator } from '@/utils/validators/complete-name.validator';
import { Button } from '@/components/ui/button/button.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';
import { Typography } from '@/components/ui/typography/typography.component';
import { getLogger } from '@/logger/logger';

interface NotifyMeData {
  name: string;
  email: string;
  city: string;
  state: string;
}

type CardStyle = 'responsive' | 'always' | 'none';

const cardStyleClasses: Record<CardStyle, string> = {
  none: '',
  always: 'border border-gray-300 bg-white rounded-lg p-6',
  responsive: 'md:border md:border-gray-300 bg-white md:rounded-lg md:p-6',
};

export interface NotifyHeaderContent {
  icon: IconDefinition;
  title: string;
  subtitle: string;
}

const defaultNotifyHeader: NotifyHeaderContent = {
  icon: faHouseChimneyMedical,
  title: 'Atendimento presencial',
  subtitle: 'Seja notificado quando uma especialidade chegar à sua cidade.',
};

interface FormNotifyMeProps {
  className?: string;
  cardStyle?: CardStyle;
  onSubmitCallback?: (data: NotifyMeData) => void | Promise<void>;
  title?: string;
  description?: string;
  headerLayout?: 'inline' | 'stacked';
  notifyHeader?: NotifyHeaderContent;
}

const formNotifyMeSchema = z.object({
  name: z.custom<string>(completeNameValidator, {
    message: 'Digite seu nome completo.',
  }),
  email: z.string().nonempty({ message: 'O campo é obrigatório' }).email({
    message: 'Digite um de e-mail válido.',
  }),
  city: z.string(),
  state: z.string(),
  acceptedTerms: z.boolean(),
  isCampaign: z.boolean(),
});

export type NotifyMeForm = z.infer<typeof formNotifyMeSchema>;

const defaultValues: Partial<NotifyMeForm> = {
  name: '',
  email: '',
  city: '',
  state: '',
  acceptedTerms: false,
  isCampaign: false,
};

export function FormNotifyMe({
  className,
  cardStyle = 'responsive',
  onSubmitCallback,
  title,
  description,
  headerLayout = 'inline',
  notifyHeader = defaultNotifyHeader,
}: FormNotifyMeProps) {
  const { toast } = useToast();
  const { startLoading, stopLoading, isLoading } = useLoading();
  const userLocation = useStore(
    userLocationStore,
    (state) => state.userLocation,
  );
  const authenticatedUser = useStore(authStore, (state) => state.authenticatedUser);

  const form = useForm<NotifyMeForm>({
    resolver: zodResolver(formNotifyMeSchema),
    defaultValues,
    mode: 'onChange',
    delayError: 100,
  });

  const { setValue } = form;

  useEffect(() => {
    if (!authenticatedUser) {
      return;
    }

    setValue('name', authenticatedUser.socialName || authenticatedUser.legalName, {
      shouldValidate: true,
    });
    setValue('email', authenticatedUser.contact?.email ?? '', {
      shouldValidate: true,
    });
  }, [authenticatedUser, setValue]);

  async function onSubmit(data: NotifyMeForm) {
    startLoading();
    const { name, email } = data;
    const city = userLocation?.city ?? '';
    const state = userLocation?.state ?? '';

    try {
      await onSubmitCallback?.({
        name,
        email,
        city,
        state,
      });
      toast.success('Informações enviadas com sucesso.');
      clearForm();
    } catch (error) {
      const logger = getLogger('FormNotifyMe/onSubmit');
      logger.warn(
        'Error while submitting the notify me form in face to face service section',
        { values: { name, email, city, state }, error },
      );
      toast.error(
        'Erro ao enviar informações. Verifique sua conexão e tente novamente.',
      );
    } finally {
      stopLoading();
    }
  }

  function clearForm() {
    form.reset(defaultValues);
  }

  const cardClassName = cardStyleClasses[cardStyle];

  return (
    <section
      data-testid={DataTestIdEnum.CITY_NOTIFY_ME_FORM}
      className={className}
    >
      <div>
        <div className={cardClassName}>
          {title
            ? (
                <div className="flex items-start flex-col gap-4 mb-6">
                  <Typography variant={TypographyVariant.MEDIUM_SUBTITLE}>
                    {title}
                  </Typography>
                  <Typography variant={TypographyVariant.MEDIUM_BODY_TEXT}>
                    {description}
                  </Typography>
                </div>
              )
            : (
                <div
                  className={
                    headerLayout === 'stacked'
                      ? 'flex flex-col items-start gap-2 mb-6'
                      : 'flex items-start gap-4 mb-6'
                  }
                >
                  <div className="w-12 h-12 aspect-square bg-success-100 rounded-full flex items-center justify-center">
                    <Icon
                      className="text-success-700 text-xl"
                      icon={notifyHeader.icon}
                    />
                  </div>

                  <div>
                    <Typography
                      variant={TypographyVariant.MEDIUM_SUBTITLE}
                      className="text-gray-900"
                    >
                      {notifyHeader.title}
                    </Typography>

                    <Typography
                      variant={TypographyVariant.MEDIUM_BODY_TEXT}
                      className={headerLayout === 'stacked' ? 'text-gray-700' : 'text-gray-600'}
                    >
                      {notifyHeader.subtitle}
                    </Typography>
                  </div>
                </div>
              )}
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} aria-label="form">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem className="mb-6">
                    <FormLabel>
                      Nome
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Digite seu nome"
                        data-testid={
                          DataTestIdEnum.CITY_NOTIFY_ME_FORM_INPUT_NAME
                        }
                        className="!mt-1"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem className="mb-6">
                    <FormLabel>
                      E-mail
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Digite seu endereço de e-mail"
                        inputMode="text"
                        data-testid={
                          DataTestIdEnum.CITY_NOTIFY_ME_FORM_INPUT_EMAIL
                        }
                        className="!mt-1"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="mb-3">
                <Typography className="text-gray-700 text-base">
                  Ao clicar em
                  {' '}
                  <span className="font-bold">Enviar</span>
                  , você
                  aceita receber novidades da Medprev.
                </Typography>
              </div>
              <Button
                data-testid={DataTestIdEnum.CITY_NOTIFY_ME_FORM_BUTTON}
                type="submit"
                variant="default"
                isLoading={isLoading}
                disabled={!form.formState.isValid}
                className="h-12"
              >
                Enviar
              </Button>
            </form>
          </Form>
        </div>
      </div>
    </section>
  );
}
